// src/modules/app-views/crm/pages/Boards/modals/CrmParametersModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Modal, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import VoipGotoTab from './VoipGotoTab';
import TemperaturesTab from './tabs/TemperaturesTab';
import SourcesTab from './tabs/SourcesTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CrmParametersModal = ({ show, setShow, afterClose, initialTab, resetInitialTab }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const hasVoipGoto = storedSubscription?.resources?.includes('moduleresources_voip_goto');
  const defaultTab = 'general';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(initialTab || defaultTab);
  const [tabLoading, setTabLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // When modal opens with initialTab, navigate to that tab
  useEffect(() => {
    if (show && initialTab) {
      setActiveTab(initialTab);
    }
  }, [show, initialTab]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab(defaultTab);
    resetInitialTab?.();
    afterClose?.();
  }, [afterClose, resetInitialTab, defaultTab]);

  const handleTabChange = useCallback(key => {
    setActiveTab(key);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'general',
      label: translateX('general'),
      children: (
        <div className="p-3">
          <p className="text-muted">{translateX('crm_parameters_description')}</p>
        </div>
      ),
    },
    {
      key: 'temperatures',
      label: translateX('temperatures'),
      children: <TemperaturesTab loading={tabLoading} setLoading={setTabLoading} />,
    },
    {
      key: 'sources',
      label: translateX('sources'),
      children: <SourcesTab loading={tabLoading} setLoading={setTabLoading} />,
    },
  ];

  // Add VOIP GoTo tab only when resource is active
  if (hasVoipGoto) {
    tabItems.push({
      key: 'voip_goto',
      label: translateX('goto_voip_config'),
      children: <VoipGotoTab />,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX('parameters')}
        </span>
      }
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      destroyOnHidden
      width={520}
      footer={null}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 110px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ top: '10vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="parameters-modal-tabs"
      />
      <style>{`
        .parameters-modal-tabs .ant-tabs-content-holder {
          flex: 1;
          overflow: hidden;
        }
        .parameters-modal-tabs .ant-tabs-content {
          height: 100%;
        }
        .parameters-modal-tabs .ant-tabs-tabpane {
          height: 100%;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CrmParametersModal;
